import { useState } from 'react'
import { Box, HStack, Text, IconButton, Progress, Button, Collapse } from '@chakra-ui/react'
import { FaTimes, FaChevronDown, FaChevronUp, FaCheck, FaExclamationTriangle } from 'react-icons/fa'
import { theme } from 'styles'
import { formatBytes, ItemIcon } from './storageUtils'

const STATUS_LABEL = {
  queued: 'Waiting',
  uploading: 'Uploading',
  finalizing: 'Finishing',
  done: 'Uploaded',
  error: 'Failed',
  canceled: 'Canceled'
}

// Floating panel in the corner listing in-flight and recent uploads.
const UploadQueue = ({ uploads, activeCount, onCancel, onClear }) => {
  const [open, setOpen] = useState(true)
  if (!uploads.length) return null
  const doneCount = uploads.filter(u => u.status === 'done').length
  const failedCount = uploads.filter(u => u.status === 'error').length

  return (
    <Box
      position="fixed" right={{ base: 2, md: 6 }} bottom={{ base: 2, md: 6 }} zIndex={30}
      width={{ base: 'calc(100% - 16px)', sm: '380px' }}
      bg="white" borderWidth="1px" borderColor={theme.line} borderRadius="12px"
      boxShadow="0 10px 30px rgba(17,24,21,.18)" overflow="hidden"
      data-testid="upload-queue"
    >
      <HStack px={4} py={2} bg={theme.greenTint} justify="space-between">
        <Text fontSize="sm" fontWeight="700" color={theme.ink}>
          {activeCount > 0
            ? `Uploading ${activeCount} file${activeCount === 1 ? '' : 's'}`
            : `${doneCount} uploaded${failedCount ? `, ${failedCount} failed` : ''}`}
        </Text>
        <HStack spacing={1}>
          {activeCount === 0 && (
            <Button size="xs" variant="ghost" onClick={onClear}>Clear</Button>
          )}
          <IconButton
            size="xs" variant="ghost" aria-label={open ? 'Collapse uploads' : 'Expand uploads'}
            icon={open ? <FaChevronDown /> : <FaChevronUp />} onClick={() => setOpen(o => !o)}
          />
        </HStack>
      </HStack>
      <Collapse in={open}>
        <Box maxH="45vh" overflowY="auto">
          {uploads.map(u => {
            const pct = u.total ? Math.min(100, Math.round((u.loaded / u.total) * 100)) : 0
            const active = ['queued', 'uploading', 'finalizing'].includes(u.status)
            return (
              <Box key={u.id} px={4} py={2} borderTopWidth="1px" borderColor={theme.line2}>
                <HStack spacing={3} align="center">
                  <ItemIcon item={{ kind: 'file', name: u.name }} size={16} />
                  <Box flex="1" minW={0}>
                    <Text fontSize="sm" fontWeight="600" noOfLines={1} title={u.name}>{u.name}</Text>
                    <Text fontSize="xs" color={u.status === 'error' ? theme.danger : theme.muted} noOfLines={1}>
                      {u.status === 'error' ? u.error : `${STATUS_LABEL[u.status]} · ${formatBytes(u.size)}${u.folderName ? ` · ${u.folderName}` : ''}`}
                    </Text>
                  </Box>
                  {u.status === 'done' && <FaCheck color={theme.green} />}
                  {u.status === 'error' && <FaExclamationTriangle color={theme.danger} />}
                  {active && (
                    <IconButton size="xs" variant="ghost" aria-label="Cancel upload" icon={<FaTimes />} onClick={() => onCancel(u.id)} />
                  )}
                </HStack>
                {active && (
                  <Progress
                    mt={1} size="xs" colorScheme="green" borderRadius="full"
                    value={pct} isIndeterminate={u.status === 'finalizing'}
                  />
                )}
              </Box>
            )
          })}
        </Box>
      </Collapse>
    </Box>
  )
}

export default UploadQueue
